import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Badge, Button, Card, CardTitle, Dialog, Field, Tabs, Textarea } from "@/core/ui";
import { getPuzzleType } from "@/puzzle-types/registry";
import { useStoredSolution } from "./api";

export interface SolutionCardProps {
  slug: string;
  /** Registry key. The card resolves the type itself; it never inspects the solution. */
  puzzleType: string;
  /** The definition currently in the editor, so the key editor can mirror it. */
  definition: unknown;
  hasSolution: boolean;
  /** Raw JSON typed by the author. Parsed by the page before saving the draft. */
  value: string;
  onChange: (value: string) => void;
  /** Whether the tenant and this user's role allow reading the stored solution back. */
  canRead: boolean;
}

export function parseSolution(text: string): { ok: true; value: unknown } | { ok: false } {
  if (!text.trim()) return { ok: true, value: undefined };
  try {
    return { ok: true, value: JSON.parse(text) as unknown };
  } catch {
    return { ok: false };
  }
}

function toText(value: unknown): string {
  return value === undefined ? "" : JSON.stringify(value, null, 2);
}

/**
 * Type-agnostic solution entry, plus editorial read-back (ADR 0015). The stored solution is
 * fetched from its own endpoint on an explicit click, never as part of the puzzle payload,
 * and only when the tenant allows it for this role.
 *
 * Two ways in, one value: the type's own key editor when it ships one, and the raw JSON that
 * has always been here. The JSON text is the single source of truth — the form parses it on
 * entry and writes it back on every change — so switching between the two never loses work.
 *
 * What comes back from the read-back endpoint is drawn by the same key editor, read-only
 * (ADR 0033): a grid of letters is a grid, not an array of strings. The raw JSON stays one
 * disclosure away, because it is what the author would paste elsewhere.
 */
export function SolutionCard({
  slug,
  puzzleType,
  definition,
  hasSolution,
  value,
  onChange,
  canRead,
}: SolutionCardProps) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const stored = useStoredSolution(slug, revealed && canRead);
  const parsed = parseSolution(value);
  const module = getPuzzleType(puzzleType);
  const storedText = toText(stored.data?.solution ?? undefined);

  const hasForm = Boolean(module?.SolutionEditor) && definition !== null;
  const storedSolution = stored.data?.solution ?? undefined;
  const canDraw = hasForm && storedSolution !== undefined;
  const form = (large: boolean) =>
    module ? (
      <SolutionForm
        module={module}
        definition={definition}
        value={value}
        onChange={onChange}
        onExpand={() => setExpanded(true)}
        expanded={large}
      />
    ) : null;

  const json = (
    <>
      <Field
        label={t("backlog.solution.json_label")}
        htmlFor="solution-json"
        hint={canRead ? t("backlog.solution.hint_readable") : t("backlog.solution.hint")}
      >
        <Textarea
          id="solution-json"
          value={value}
          spellCheck={false}
          aria-invalid={!parsed.ok}
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
      <div aria-live="polite">
        {!parsed.ok ? <Alert tone="danger">{t("backlog.solution.invalid_json")}</Alert> : null}
      </div>
    </>
  );

  return (
    <Card>
      <CardTitle>{t("backlog.solution.title")}</CardTitle>
      <p className="bt-solution__state">
        <Badge tone={hasSolution ? "success" : "neutral"}>
          {hasSolution ? t("backlog.solution.stored") : t("backlog.solution.missing")}
        </Badge>
        {hasSolution && canRead ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => setRevealed(!revealed)}
            loading={revealed && stored.isPending}
            aria-expanded={revealed}
            aria-controls="stored-solution"
          >
            {revealed ? t("backlog.solution.hide") : t("backlog.solution.reveal")}
          </Button>
        ) : null}
      </p>
      {revealed && canRead ? (
        <div id="stored-solution" className="bt-solution__readback">
          {stored.isError ? (
            <Alert tone="danger">{t("backlog.solution.read_failed")}</Alert>
          ) : (
            <>
              {canDraw && module ? (
                <SolutionView module={module} definition={definition} value={storedSolution} />
              ) : null}
              {canDraw ? (
                <details className="bt-solution__raw">
                  <summary>{t("backlog.solution.raw_json")}</summary>
                  <pre className="bt-solution__dump">{storedText}</pre>
                </details>
              ) : (
                <pre className="bt-solution__dump">{storedText}</pre>
              )}
              <div className="bt-solution__readback-actions">
                <p className="bt-solution__note">
                  {t("backlog.solution.version", { number: stored.data?.version_number ?? "" })}
                </p>
                {storedText ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onChange(storedText)}
                  >
                    {t("backlog.solution.start_from_stored")}
                  </Button>
                ) : null}
              </div>
            </>
          )}
        </div>
      ) : null}

      {hasForm ? (
        <Tabs
          items={[
            {
              id: "form",
              label: t("backlog.solution.tab_form"),
              // Only one copy of the editor is alive at a time: the larger view takes over.
              content: expanded ? (
                <p className="bt-solution__note">{t("backlog.solution.editing_larger")}</p>
              ) : (
                form(false)
              ),
            },
            { id: "json", label: t("backlog.solution.tab_json"), content: json },
          ]}
        />
      ) : (
        json
      )}

      <div aria-live="polite">
        {parsed.ok && value.trim() ? (
          <p className="bt-solution__note">{t("backlog.solution.queued")}</p>
        ) : null}
      </div>

      {hasForm && expanded ? (
        <Dialog
          open
          onClose={() => setExpanded(false)}
          title={t("backlog.solution.title")}
          className="bt-dialog--wide"
        >
          {form(true)}
        </Dialog>
      ) : null}
    </Card>
  );
}

type PuzzleModule = NonNullable<ReturnType<typeof getPuzzleType>>;

/**
 * Draws the stored key with the type's own editor, read-only. It never reports a change: the
 * way to work from this key is the "use as a starting point" button, which copies it into the
 * JSON the form edits.
 */
function SolutionView({
  module,
  definition,
  value,
}: {
  module: PuzzleModule;
  definition: unknown;
  value: unknown;
}) {
  const { t, i18n } = useTranslation();
  const Editor = module.SolutionEditor;
  if (!Editor) return null;
  return (
    <div className="bt-solution__view">
      <Suspense fallback={<p className="bt-solution__note">{t("app.loading")}</p>}>
        <Editor
          definition={definition}
          value={value}
          onChange={noop}
          locale={i18n.language}
          readOnly
        />
      </Suspense>
    </div>
  );
}

function noop(): void {}

/**
 * Hosts the type's key editor. Mounted fresh whenever the form tab or the larger view opens,
 * which is what keeps it in step with a JSON edit made in the other tab.
 */
function SolutionForm({
  module,
  definition,
  value,
  onChange,
  onExpand,
  expanded,
}: {
  module: PuzzleModule;
  definition: unknown;
  value: string;
  onChange: (value: string) => void;
  onExpand: () => void;
  expanded: boolean;
}) {
  const { t, i18n } = useTranslation();
  const parsed = parseSolution(value);
  const Editor = module.SolutionEditor;
  if (!Editor) return null;

  if (!parsed.ok) {
    return (
      <div className="bt-solution__blocked">
        <Alert tone="danger">{t("backlog.solution.form_blocked")}</Alert>
        <Button type="button" size="sm" variant="outline" onClick={() => onChange("")}>
          {t("backlog.solution.discard_json")}
        </Button>
      </div>
    );
  }

  const current = parsed.value ?? module.emptySolution?.(definition);

  return (
    <div className="bt-solution__form">
      <Suspense fallback={<p className="bt-solution__note">{t("app.loading")}</p>}>
        <Editor
          definition={definition}
          value={current}
          onChange={(next: unknown) => onChange(toText(next))}
          locale={i18n.language}
        />
      </Suspense>
      <div className="bt-solution__form-actions">
        <Button type="button" size="sm" variant="ghost" onClick={() => onChange("")}>
          {t("backlog.solution.clear")}
        </Button>
        {expanded ? null : (
          <Button type="button" size="sm" variant="outline" onClick={onExpand}>
            {t("backlog.solution.expand")}
          </Button>
        )}
      </div>
    </div>
  );
}
